import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from '@structural-climbing/ui'
import { MemberIdentity } from '../members/MemberIdentity'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import type { MemberHit } from './types'

const SEARCH_DEBOUNCE_MS = 250
const MIN_CHARS = 2

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Opens the cart. Rejects when it could not be opened. */
  onOpen: (target: { userId: string } | { label: string }) => Promise<void>
  /**
   * `assign` moves a quick sale onto a member: members only, no guest name,
   * since the cart is already a walk-in's.
   */
  mode?: 'open' | 'assign'
}

/**
 * Who is the cart for? Type a few letters and tap the member, or type a name
 * for someone who is not one. Either way it is two actions to an open cart.
 */
export function OpenCartModal({ isOpen, onClose, onOpen, mode = 'open' }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const { primaryColor } = useGymThemeContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<MemberHit[]>([])
  const [searching, setSearching] = useState(false)
  const [guestName, setGuestName] = useState('')
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setQuery('')
    setHits([])
    setGuestName('')
    setError(null)
  }, [isOpen])

  const term = query.trim()
  useEffect(() => {
    if (!isOpen || term.length < MIN_CHARS) {
      setHits([])
      setSearching(false)
      return
    }
    setSearching(true)
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/carts/member-search?q=${encodeURIComponent(term)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        setHits(res.ok ? ((await res.json()) as MemberHit[]) : [])
        setSearching(false)
      } catch {
        // Aborted by the next keystroke: that search owns the spinner now.
        if (!controller.signal.aborted) setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [isOpen, term, apiUrl, getAccessTokenSilently])

  async function open(target: { userId: string } | { label: string }) {
    setOpening(true)
    setError(null)
    try {
      await onOpen(target)
      onClose()
    } catch {
      setError(t(mode === 'assign' ? 'products.errorAssign' : 'products.errorOpen'))
    } finally {
      setOpening(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      scrollBehavior="inside"
      placement="top"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              {t(mode === 'assign' ? 'products.assignTitle' : 'products.openCartTitle')}
            </ModalHeader>
            <ModalBody className="gap-5 pb-6">
              <div className="flex flex-col gap-2">
                <Input
                  autoFocus
                  label={t('products.memberSearch')}
                  placeholder={t('products.memberSearchPlaceholder')}
                  value={query}
                  onValueChange={setQuery}
                />
                {searching && (
                  <div className="flex justify-center py-2">
                    <Spinner size="sm" color="primary" />
                  </div>
                )}
                {!searching && term.length >= MIN_CHARS && hits.length === 0 && (
                  <p className="py-2 text-center text-sm text-foreground/50">
                    {t('products.noMembers')}
                  </p>
                )}
                {hits.map((hit) => (
                  <Button
                    key={hit.user_id}
                    variant="flat"
                    className="h-auto justify-start px-3 py-2 text-left"
                    isDisabled={opening}
                    onPress={() => void open({ userId: hit.user_id })}
                  >
                    <MemberIdentity
                      userId={hit.user_id}
                      name={hit.name}
                      nickname={hit.nickname}
                      email={hit.email}
                      avatarExt={hit.avatar_ext}
                      avatarUpdatedAt={hit.avatar_updated_at}
                      primaryColor={primaryColor}
                    />
                  </Button>
                ))}
              </div>

              {mode === 'open' && (
                <form
                  className="flex flex-col gap-2 border-t border-divider pt-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void open({ label: guestName.trim() || t('products.guest') })
                  }}
                >
                  <p className="text-sm font-medium">{t('products.guestTitle')}</p>
                  <Input
                    label={t('products.guestName')}
                    description={t('products.guestNameHint')}
                    value={guestName}
                    onValueChange={setGuestName}
                    maxLength={60}
                  />
                  <Button type="submit" variant="flat" isDisabled={opening}>
                    {t('products.openGuestCart')}
                  </Button>
                </form>
              )}

              {error && <p className="text-sm text-danger">{error}</p>}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
